import React, { useState } from 'react';
import {
  Download,
  Calendar,
  Filter,
  FileText,
  Table,
  BarChart2,
  Settings,
  ChevronDown,
  Check,
  X,
  AlertTriangle,
  RefreshCw,
  Eye,
  Clock,
  Search
} from 'lucide-react';

interface ExportConfig {
  format: 'csv' | 'xlsx' | 'json' | 'pdf';
  dateRange: {
    start: string;
    end: string;
  };
  stages: string[];
  metrics: string[];
  aggregation: 'raw' | 'hourly' | 'daily' | 'weekly' | 'monthly';
  includeCharts: boolean;
  includeStats: boolean;
}

interface ExportJob {
  id: string;
  name: string;
  format: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  createdAt: string;
  completedAt?: string;
  downloadUrl?: string;
  error?: string;
}

export const ExportData: React.FC = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    format: 'csv',
    dateRange: {
      start: '',
      end: ''
    },
    stages: [],
    metrics: [],
    aggregation: 'raw',
    includeCharts: true,
    includeStats: true
  });

  const [recentExports, setRecentExports] = useState<ExportJob[]>([
    {
      id: '1',
      name: 'Daily Performance Report',
      format: 'xlsx',
      status: 'completed',
      progress: 100,
      createdAt: '2024-03-14T10:30:00',
      completedAt: '2024-03-14T10:31:00',
      downloadUrl: '#'
    },
    {
      id: '2',
      name: 'Monthly Analytics Export',
      format: 'csv',
      status: 'processing',
      progress: 65,
      createdAt: '2024-03-14T10:29:00'
    },
    {
      id: '3',
      name: 'System Metrics',
      format: 'json',
      status: 'failed',
      progress: 0,
      createdAt: '2024-03-14T10:25:00',
      error: 'Connection timeout'
    }
  ]);

  const stages = [
    'Raw Water Stage',
    'Pre-Treatment Stage',
    'Ultra-Filtration Stage',
    'De-Chlorination Stage',
    'Reverse Osmosis Stage',
    'RO Product Stage'
  ];

  const metrics = [
    'Flow Rate',
    'Pressure',
    'Temperature',
    'pH',
    'Conductivity',
    'Turbidity',
    'TDS',
    'Power Consumption',
    'Recovery Rate',
    'Efficiency'
  ];

  const handleExport = async () => {
    if (!exportConfig.dateRange.start || !exportConfig.dateRange.end) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    setIsExporting(true);
    
    try {
      // Simulate export process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const newExport: ExportJob = {
        id: String(recentExports.length + 1),
        name: `Data Export ${new Date().toLocaleString()}`,
        format: exportConfig.format,
        status: 'completed',
        progress: 100,
        createdAt: new Date().toISOString(),
        completedAt: new Date().toISOString(),
        downloadUrl: '#'
      };
      
      setRecentExports(prev => [newExport, ...prev]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
    
    setIsExporting(false);
  };

  const toggleStage = (stage: string) => {
    setExportConfig(prev => ({
      ...prev,
      stages: prev.stages.includes(stage)
        ? prev.stages.filter(s => s !== stage)
        : [...prev.stages, stage]
    }));
  };

  const toggleMetric = (metric: string) => {
    setExportConfig(prev => ({
      ...prev,
      metrics: prev.metrics.includes(metric)
        ? prev.metrics.filter(m => m !== metric)
        : [...prev.metrics, metric]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Export Configuration */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Download className="h-6 w-6 text-gray-600" />
            <h2 className="text-xl font-bold text-gray-900">Export Data</h2>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
            >
              <Eye className="h-4 w-4" />
              <span>Preview</span>
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                isExporting
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isExporting ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export'}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Settings */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Export Settings</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                <div className="grid grid-cols-4 gap-2">
                  {['csv', 'xlsx', 'json', 'pdf'].map(format => (
                    <button
                      key={format}
                      onClick={() => setExportConfig(prev => ({ ...prev, format: format as ExportConfig['format'] }))}
                      className={`flex items-center justify-center space-x-2 p-2 rounded-lg border-2 ${
                        exportConfig.format === format
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-200'
                      }`}
                    >
                      <FileText className="h-4 w-4" />
                      <span className="text-sm uppercase">{format}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <div className="relative">
                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {exportConfig.dateRange.start && exportConfig.dateRange.end
                          ? `${exportConfig.dateRange.start} to ${exportConfig.dateRange.end}`
                          : 'Select date range'}
                      </span>
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </button>

                  {showDatePicker && (
                    <div className="absolute left-0 mt-2 w-full bg-white rounded-lg shadow-lg p-4 z-10 border border-gray-200">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                          <input
                            type="date"
                            value={exportConfig.dateRange.start}
                            onChange={(e) => setExportConfig(prev => ({
                              ...prev,
                              dateRange: { ...prev.dateRange, start: e.target.value }
                            }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            type="date"
                            value={exportConfig.dateRange.end}
                            onChange={(e) => setExportConfig(prev => ({
                              ...prev,
                              dateRange: { ...prev.dateRange, end: e.target.value }
                            }))}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={() => setShowDatePicker(false)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Aggregation</label>
                <select
                  value={exportConfig.aggregation}
                  onChange={(e) => setExportConfig(prev => ({
                    ...prev,
                    aggregation: e.target.value as ExportConfig['aggregation']
                  }))}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="raw">Raw Data</option>
                  <option value="hourly">Hourly Average</option>
                  <option value="daily">Daily Average</option>
                  <option value="weekly">Weekly Average</option>
                  <option value="monthly">Monthly Average</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfig.includeCharts}
                    onChange={(e) => setExportConfig(prev => ({
                      ...prev,
                      includeCharts: e.target.checked
                    }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Include Charts</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={exportConfig.includeStats}
                    onChange={(e) => setExportConfig(prev => ({
                      ...prev,
                      includeStats: e.target.checked
                    }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Include Statistics</span>
                </label>
              </div>
            </div>
          </div>

          {/* Data Selection */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-4">Data Selection</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Stages</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {stages.map(stage => (
                    <label key={stage} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={exportConfig.stages.includes(stage)}
                        onChange={() => toggleStage(stage)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">{stage}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Metrics</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {metrics.map(metric => (
                    <label key={metric} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={exportConfig.metrics.includes(metric)}
                        onChange={() => toggleMetric(metric)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">{metric}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Exports */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Exports</h3>
        <div className="space-y-4">
          {recentExports.map(job => (
            <div
              key={job.id}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-full ${
                  job.status === 'completed'
                    ? 'bg-green-100'
                    : job.status === 'processing'
                    ? 'bg-blue-100'
                    : job.status === 'failed'
                    ? 'bg-red-100'
                    : 'bg-gray-100'
                }`}>
                  {job.status === 'completed' ? (
                    <Check className={`h-5 w-5 text-green-600`} />
                  ) : job.status === 'processing' ? (
                    <RefreshCw className={`h-5 w-5 text-blue-600 animate-spin`} />
                  ) : job.status === 'failed' ? (
                    <X className={`h-5 w-5 text-red-600`} />
                  ) : (
                    <Clock className={`h-5 w-5 text-gray-600`} />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{job.name}</p>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span className="uppercase">{job.format}</span>
                    <span>•</span>
                    <span>{new Date(job.createdAt).toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {job.status === 'processing' && (
                  <div className="w-32">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  </div>
                )}
                {job.status === 'completed' && job.downloadUrl && (
                  <a
                    href={job.downloadUrl}
                    download
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </a>
                )}
                {job.status === 'failed' && job.error && (
                  <span className="flex items-center space-x-2 text-sm text-red-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>{job.error}</span>
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 flex items-center space-x-2 bg-green-50 p-4 rounded-lg border border-green-200">
          <Check className="h-5 w-5 text-green-500" />
          <span className="text-green-700">Export completed successfully</span>
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div className="fixed bottom-4 right-4 flex items-center space-x-2 bg-red-50 p-4 rounded-lg border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">Please select a date range</span>
        </div>
      )}
    </div>
  );
};